import React, { useState, useEffect } from 'react';
import { FaGraduationCap, FaCheck, FaTimes, FaArrowRight } from 'react-icons/fa';

const QuizMode = ({ model, simulationData }) => {
  const [quizActive, setQuizActive] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  
  // Quiz questions database
  const questions = [
    {
      question: "What happens to the slip power in a wound rotor induction motor?",
      options: [
        "It is dissipated as heat in the rotor",
        "It is recovered and fed back to the grid",
        "It is stored in capacitors",
        "It is always zero"
      ],
      correctAnswer: 1,
      explanation: "In slip power recovery systems, the power that would normally be lost as heat in the rotor is recovered and fed back to the grid or mechanical system."
    },
    {
      question: "Which of the following is NOT a component of a Scherbius drive?",
      options: [
        "Wound rotor induction motor",
        "AC-DC converter",
        "DC-AC inverter",
        "DC motor"
      ],
      correctAnswer: 3,
      explanation: "The Scherbius drive uses AC-DC-AC conversion to feed power back to the grid, while the Kramer drive uses a DC motor."
    },
    {
      question: "As slip increases in a slip power recovery system, what happens to the recoverable power?",
      options: [
        "It decreases",
        "It increases",
        "It remains constant",
        "It becomes negative"
      ],
      correctAnswer: 1,
      explanation: "As slip increases, more power flows through the rotor circuit, increasing the amount of power that can be recovered."
    },
    {
      question: "What is the main advantage of a closed-loop control system in slip power recovery?",
      options: [
        "Lower cost",
        "Simpler implementation",
        "Better speed regulation",
        "Higher maximum speed"
      ],
      correctAnswer: 2,
      explanation: "Closed-loop control provides better speed regulation by continuously monitoring and adjusting the system based on feedback."
    },
    {
      question: "In which speed range is slip power recovery most beneficial?",
      options: [
        "Above synchronous speed",
        "At synchronous speed",
        "Slightly below synchronous speed",
        "Far below synchronous speed"
      ],
      correctAnswer: 3,
      explanation: "Slip power recovery is most beneficial when operating far below synchronous speed, where significant power flows through the rotor circuit."
    }
  ];
  
  const startQuiz = () => {
    setQuizActive(true);
    setCurrentQuestion(0);
    setScore(0);
    setShowResult(false);
    setSelectedAnswer(null);
  };
  
  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };
  
  const nextQuestion = () => {
    // Update score if answer is correct
    if (selectedAnswer === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
    
    // Move to next question or end quiz
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
    } else {
      setShowResult(true);
    }
  };
  
  const resetQuiz = () => {
    setQuizActive(false);
    setShowResult(false);
  };
  
  // Generate a contextual question based on current simulation state
  useEffect(() => {
    if (simulationData && model) {
      // Could dynamically modify questions based on current simulation state
      // For example, add a question about the current drive type or slip value
    }
  }, [simulationData, model]);
  
  return (
    <div className="quiz-mode glass-panel">
      <div className="quiz-header">
        <FaGraduationCap size={24} />
        <h3>Quiz Mode</h3>
      </div>
      
      {!quizActive && !showResult ? (
        <div className="quiz-intro">
          <p>Test your knowledge about Slip Power Recovery systems!</p>
          <button className="start-quiz-btn" onClick={startQuiz}>Start Quiz</button>
        </div>
      ) : showResult ? (
        <div className="quiz-results">
          <h4>Quiz Complete!</h4>
          <p>Your score: {score} out of {questions.length}</p>
          <p>{score / questions.length >= 0.7 ? "Great job!" : "Keep learning!"}</p>
          <button className="reset-quiz-btn" onClick={resetQuiz}>Try Again</button>
        </div>
      ) : (
        <div className="quiz-question">
          <div className="question-counter">
            Question {currentQuestion + 1} of {questions.length}
          </div>
          
          <h4>{questions[currentQuestion].question}</h4>
          
          <div className="answer-options">
            {questions[currentQuestion].options.map((option, index) => (
              <div 
                key={index} 
                className={`answer-option ${selectedAnswer === index ? 'selected' : ''}`}
                onClick={() => handleAnswerSelect(index)}
              >
                {option}
              </div>
            ))}
          </div>
          
          {selectedAnswer !== null && (
            <div className="explanation">
              {selectedAnswer === questions[currentQuestion].correctAnswer ? (
                <div className="correct-answer">
                  <FaCheck /> Correct!
                </div>
              ) : (
                <div className="incorrect-answer">
                  <FaTimes /> Incorrect. The correct answer is: {questions[currentQuestion].options[questions[currentQuestion].correctAnswer]}
                </div>
              )}
              <p>{questions[currentQuestion].explanation}</p>
            </div>
          )}
          
          <button 
            className="next-question-btn" 
            onClick={nextQuestion} 
            disabled={selectedAnswer === null}
          >
            {currentQuestion < questions.length - 1 ? "Next Question" : "Finish Quiz"} <FaArrowRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default QuizMode;